import orderBy from 'lodash/orderBy';
import type { ImageSizeKeysTypes } from '@axiom/const';
import { CandidateImageConst, WeeklyTimeUnits } from '@axiom/const';
import type { Candidate, FullCandidateAccount } from '@axiom/validation';

import { EnvUtil } from './env-util';

const { IMAGE_RESIZE_DIRECTORY, ImageSizesByWidth, PROFILE_IMAGE_DIRECTORY } =
  CandidateImageConst;

export const CandidateUtil = {
  formatCandidateLocation({
    addressCity,
    addressState,
    addressCountry,
  }: Pick<Candidate, 'addressCity' | 'addressState' | 'addressCountry'>) {
    const location = [];
    if (addressCity) {
      location.push(addressCity);
    }
    if (addressState) {
      location.push(addressState);
    }
    if (addressCountry) {
      location.push(addressCountry);
    }

    return location.length > 0 ? location.join(', ') : null;
  },

  getFormattedLawFirms({ lawFirms }: Pick<Candidate, 'lawFirms'> = {}) {
    return lawFirms?.length && lawFirms.length > 3
      ? [...lawFirms.slice(0, 3), { client: '...' }]
      : lawFirms;
  },

  formatYOE(years: string) {
    const numYears = Number.parseInt(years, 10);
    let suffix = ' yr.';
    let prefix = '';

    if (numYears < 1) {
      prefix = '< ';
    } else if (numYears < 2) {
      suffix = '+ yr.';
    }
    if (numYears >= 2) suffix = '+ yrs.';

    return `${prefix}${numYears >= 2 ? numYears : 1}${suffix}`;
  },

  getProfileImageUri(
    { profileImageKey }: Pick<Candidate, 'profileImageKey'> = {
      profileImageKey: null,
    },
    size: ImageSizeKeysTypes = 'W_100'
  ) {
    if (!profileImageKey || !EnvUtil.publicS3Bucket) {
      return null;
    }

    const [idSlug] = profileImageKey
      .replace(`${PROFILE_IMAGE_DIRECTORY}/`, '')
      .split('/');
    const { width, height } = ImageSizesByWidth[size];

    return `${EnvUtil.publicBaseUrlS3}/${EnvUtil.publicS3Bucket}/${IMAGE_RESIZE_DIRECTORY}/${idSlug}/${width}x${height}`;
  },

  calculateBillingHoursPerWeek(
    candidate:
      | (Candidate & {
          billingUnitsPerWeek: number;
          weeklyTimeUnit: (typeof WeeklyTimeUnits)[keyof typeof WeeklyTimeUnits];
        })
      | FullCandidateAccount
  ) {
    const { billingUnitsPerWeek, weeklyTimeUnit } = candidate;

    return (
      (billingUnitsPerWeek ?? 0) *
      (weeklyTimeUnit === WeeklyTimeUnits.Daily
        ? 8
        : (billingUnitsPerWeek ?? 0))
    );
  },

  getUniqueLawDegreeSchools(
    lawDegreeSchools:
      | Array<
          Pick<
            NonNullable<Candidate['lawDegreeSchools']>[number],
            'institution'
          >
        >
      | undefined
      | null
  ) {
    return [...new Set(lawDegreeSchools?.map(item => item.institution))];
  },

  formatTalentSubtitle(
    legalRole: string | null,
    yearsOfExperienceRange: string
  ) {
    const parts = [
      legalRole,
      yearsOfExperienceRange === '--'
        ? null
        : `${yearsOfExperienceRange} years experience`,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' with ') : null;
  },

  formatIndustries(industries: Candidate['industries']) {
    const displayableIndustries = (industries ?? []).filter(
      i => i.industryValue.toLowerCase() !== 'other'
    );
    return orderBy(
      displayableIndustries.map(industry => ({
        ...industry,
        yearsOfExperience: Number.parseInt(industry.yearsOfExperience, 10),
      })),
      ['yearsOfExperience', 'industryValue'],
      ['desc', 'asc']
    );
  },

  getYearsOfExperienceRange(
    yearsOfExperience: number | null | undefined
  ): string {
    if (yearsOfExperience === null || yearsOfExperience === undefined) {
      return '--';
    }
    if (yearsOfExperience >= 20) return '20+';
    if (yearsOfExperience >= 16) return '16 - 19';
    if (yearsOfExperience >= 12) return '12 - 15';
    if (yearsOfExperience >= 8) return '8 - 11';
    return yearsOfExperience >= 4 ? '4 - 7' : '0 - 3';
  },
};
